'use client'

import React from 'react'
import {usePaymentAddress} from './payment-address-context'
import {useBillingInfo} from './billing-info-context'
import {usePaymentCard} from './payment-card-context'
import {useRecurlyPayment} from './recurly-payment-context'
import {usePaymentBankAccount} from './payment-bank-account-context'
import {usePaymentBecsBankAccount} from './payment-becs-bank-account-context'

/**
 * @typedef {{
 * checkBoxesValues: {
 *      agreedTerms: boolean,
 *      agreedPromotionalInfo: boolean,
 *      useBusinessAddressInfo: boolean,
 *      updateBillingAddress: boolean,
 * },
 * updateCheckbox: (field: 'agreedTerms' | 'agreedPromotionalInfo' | 'useBusinessAddressInfo' | 'updateBillingAddress', value: boolean) => void,
 * updateMultipleCheckboxes: (updates: object) => void,
 * resetCheckboxes: () => void,
 * setCheckBoxesValues: (value: {
 *      agreedTerms: boolean,
 *      agreedPromotionalInfo: boolean,
 *      useBusinessAddressInfo: boolean,
 *      updateBillingAddress: boolean,
 * })=>void;
 * isAgreeCheckboxDisabled: boolean;
 * }} PaymentCheckboxesContext
 */

const PaymentCheckboxesContext = React.createContext(null)

const initialCheckboxState = {
  agreedTerms: false,
  agreedPromotionalInfo: false,
  useBusinessAddressInfo: false,
  updateBillingAddress: false,
}

export function PaymentCheckboxesProvider({children}) {
  const {isValidAddress} = usePaymentAddress()
  const {isValidCard} = usePaymentCard()
  const {isCVCVerified} = useBillingInfo()
  const {isValidBankAccount} = usePaymentBankAccount()
  const {isValidBecsBankAccount} = usePaymentBecsBankAccount()
  const {type} = useRecurlyPayment()
  const [checkBoxesValues, setCheckBoxesValues] =
    React.useState(initialCheckboxState)

  const updateCheckbox = React.useCallback((field, value) => {
    setCheckBoxesValues(prevState => ({
      ...prevState,
      [field]: value,
    }))
  }, [])

  const updateMultipleCheckboxes = React.useCallback(updates => {
    setCheckBoxesValues(prevState => ({
      ...prevState,
      ...updates,
    }))
  }, [])

  const resetCheckboxes = React.useCallback(() => {
    setCheckBoxesValues(initialCheckboxState)
  }, [])

  const value = React.useMemo(
    () => ({
      checkBoxesValues,
      updateCheckbox,
      updateMultipleCheckboxes,
      resetCheckboxes,
      setCheckBoxesValues, // Keep for backward compatibility
      isAgreeCheckboxDisabled:
        type === 'card'
          ? !isCVCVerified && !(isValidAddress && isValidCard)
          : type === 'ach'
          ? !(isValidAddress && isValidBankAccount)
          : type === 'invoice'
          ? !isValidAddress
          : !(isValidAddress && isValidBecsBankAccount),
    }),
    [
      checkBoxesValues,
      updateCheckbox,
      updateMultipleCheckboxes,
      resetCheckboxes,
      setCheckBoxesValues,
      isValidAddress,
      isCVCVerified,
      isValidCard,
      isValidBankAccount,
      isValidBecsBankAccount,
      type,
    ],
  )

  return (
    <PaymentCheckboxesContext.Provider value={value}>
      {children}
    </PaymentCheckboxesContext.Provider>
  )
}

/**
 * @returns {PaymentCheckboxesContext}
 */
export function usePaymentCheckboxes() {
  const context = React.useContext(PaymentCheckboxesContext)
  if (!context) {
    throw new Error(
      "'usePaymentCheckboxes' can't be called outside 'PaymentCheckboxesProvider'",
    )
  }
  return context
}
